import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/http";
import { useStore, useT } from "../store/useStore";
import { Btn, Icon, IconBtn } from "../components/ui";
import { AvatarMenu } from "./AvatarMenu";
import { CommandBar } from "./CommandBar";
import { Logo } from "./Logo";
import { logoAdresse } from "../lib/kommune";
import { warmProjekt, warmRouteChunk } from "./ProjectSwitcher";

export function TopNav() {
  const t = useT();
  const de = useStore((s) => s.lang) === "de";
  const auth = useStore((s) => s.auth);
  const theme = useStore((s) => s.theme);
  const toggleTheme = useStore((s) => s.toggleTheme);
  const lastProjectId = useStore((s) => s.lastProjectId);
  const setLastProject = useStore((s) => s.setLastProject);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const qc = useQueryClient();

  // desktop project switcher — only where a single project is the active context
  const projectsQ = useQuery({ queryKey: ["projects"], queryFn: () => api.projects.list(), enabled: !!auth });
  const inProjectContext = pathname.startsWith("/project") || pathname.startsWith("/karte") || pathname.startsWith("/admin") || pathname.startsWith("/uebersicht");
  // stale lastProject (e.g. the admin unassigned this user from it) → snap to the first accessible one
  useEffect(() => {
    const ps = projectsQ.data;
    if (ps && ps.length && (!lastProjectId || !ps.some((p) => p.id === lastProjectId))) setLastProject(ps[0]!.id);
  }, [projectsQ.data, lastProjectId, setLastProject]);
  const showSwitcher = !!auth && (projectsQ.data?.length ?? 0) > 1 && inProjectContext;
  // Kommune, an der gerade gearbeitet wird — ihr Logo steht neben dem Produktnamen
  const kommune = auth ? projectsQ.data?.find((p) => p.id === lastProjectId) : undefined;
  /* Der Umschalter fuehrt dorthin, wo man gerade steht: von der Karte auf die
     Karte, sonst ins Dashboard. Der Vorgriff muss demselben Zweig folgen,
     sonst waermt er den falschen Chunk. */
  const vorgriffProjekt = (id: string) => {
    const aufKarte = pathname.startsWith("/karte");
    warmRouteChunk(aufKarte ? "karte" : "stats");
    warmProjekt(qc, id, !aufKarte);
  };
  const onPickProject = (id: string) => {
    setLastProject(id);
    // jump to the same kind of screen for the new project; admin/other just adopt lastProject
    if (pathname.startsWith("/karte") || pathname.startsWith("/uebersicht")) navigate(`/karte?project=${id}`);
    else if (pathname.startsWith("/project")) navigate(`/project/${id}`);
  };

  /* Vorgriff je Ziel: Chunk der Ansicht + die Abfragen des aktiven Projekts.
     „Dashboard" fuehrt ueber "/" sofort auf /project/:id weiter — es ist also
     der ProjectStats-Chunk (mit echarts, der vierten seriellen Stufe), der dort
     gebraucht wird, nicht irgendeine Landeseite. Die Karte kommt ohne stats
     aus, das ist die teuerste der drei Abfragen. */
  const warmDashboard = () => {
    warmRouteChunk("stats");
    warmProjekt(qc, lastProjectId);
  };
  const warmKarte = () => {
    warmRouteChunk("karte");
    warmProjekt(qc, lastProjectId, false);
  };

  const navItems = [
    {
      id: "dashboard",
      icon: "squares-four",
      label: t("nav.dashboard"),
      to: "/",
      on: pathname === "/" || pathname.startsWith("/project"),
      warm: warmDashboard,
    },
    // carry the active project so the map opens the right one (not a hardcoded default)
    { id: "karte", icon: "map-trifold", label: t("nav.karte"), to: lastProjectId ? `/karte?project=${lastProjectId}` : "/karte", on: pathname.startsWith("/karte"), warm: warmKarte },
    // Meldeportal: nur, wenn mindestens eine Kommune den Baustein Bürgermeldungen gebucht hat
    ...((projectsQ.data ?? []).some((p) => (p.addons ?? []).includes("meldungen"))
      ? [{ id: "meldeportal", icon: "chat-circle-dots", label: de ? "Meldeportal" : "Reports", to: "/meldeportal", on: pathname.startsWith("/meldeportal"), warm: () => undefined }]
      : []),
  ];

  return (
    <header
      className="glass"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: "var(--kopf-h)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "var(--safe-top) 18px 0",
        borderBottom: "1px solid var(--glass-border)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Logo onClick={() => navigate("/")} />
        {/* auf weissem Grund: dunkle Logos bleiben so auch im dunklen Thema lesbar */}
        {kommune?.logo && (
          <img
            className="kommune-logo"
            src={logoAdresse(kommune.id, kommune.logoSig)}
            alt={kommune.name}
            title={kommune.name}
            style={{ height: 32, maxWidth: 140, objectFit: "contain", background: "#fff", borderRadius: 8, padding: "3px 7px", border: "1px solid var(--glass-border)", display: "block", boxSizing: "border-box" }}
          />
        )}
        {auth && (
          <nav style={{ display: "flex", gap: 4, marginLeft: 6 }} className="topnav">
            {/* onMouseEnter UND onFocus tragen den Vorgriff: ohne onFocus
                haetten Tastaturnutzer nichts davon, sie erreichen den Knopf
                nur ueber den Tabulator. */}
            {navItems.map((n) => (
              <button
                key={n.id}
                onClick={() => navigate(n.to)}
                className="ring-focus"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "7px 14px",
                  borderRadius: 10,
                  border: "none",
                  cursor: "pointer",
                  fontSize: 13.5,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  transition: "all .15s",
                  background: n.on ? "var(--accent)" : "transparent",
                  color: n.on ? "#fff" : "var(--text-dim)",
                }}
                onMouseEnter={(e) => {
                  if (!n.on) e.currentTarget.style.background = "var(--hover)";
                  n.warm();
                }}
                onFocus={() => n.warm()}
                onMouseLeave={(e) => {
                  if (!n.on) e.currentTarget.style.background = "transparent";
                }}
              >
                <Icon name={n.icon} size={17} />
                <span className="topnav-label">{n.label}</span>
              </button>
            ))}
          </nav>
        )}
        {auth?.role === "admin" && (
          <button
            onClick={() => navigate("/uebersicht")}
            className="ring-focus uebersicht-mobil"
            title="Übersicht — alle Kommunen"
            aria-label="Übersicht — alle Kommunen"
            aria-current={pathname.startsWith("/uebersicht") ? "true" : undefined}
            style={{
              alignItems: "center", justifyContent: "center",
              padding: "7px 10px", borderRadius: 10, border: "none",
              background: pathname.startsWith("/uebersicht") ? "var(--accent)" : "transparent",
              color: pathname.startsWith("/uebersicht") ? "#fff" : "var(--accent-text)",
              cursor: "pointer", transition: "all .15s",
            }}
          >
            <Icon name="map-pin-area" size={17} weight="fill" />
          </button>
        )}
        {showSwitcher && (
          <div className="psw-nav" style={{ display: "flex", alignItems: "center", gap: 5, marginLeft: 8, paddingLeft: 11, borderLeft: "1px solid var(--glass-border)" }}>
            {auth?.role === "admin" ? (
              <button
                onClick={() => navigate("/uebersicht")}
                className="ring-focus"
                title="Übersicht — alle Kommunen"
                aria-label="Übersicht — alle Kommunen"
                aria-current={pathname.startsWith("/uebersicht") ? "true" : undefined}
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  padding: "4px 7px", borderRadius: 9, border: "1px solid",
                  borderColor: pathname.startsWith("/uebersicht") ? "var(--accent-ring)" : "transparent",
                  background: pathname.startsWith("/uebersicht") ? "var(--accent)" : "transparent",
                  color: pathname.startsWith("/uebersicht") ? "#fff" : "var(--accent-text)",
                  cursor: "pointer", flex: "none", marginRight: 1, transition: "all .15s",
                }}
                onMouseEnter={(e) => { if (!pathname.startsWith("/uebersicht")) e.currentTarget.style.background = "var(--hover)"; }}
                onMouseLeave={(e) => { if (!pathname.startsWith("/uebersicht")) e.currentTarget.style.background = "transparent"; }}
              >
                <Icon name="map-pin-area" size={15} weight="fill" />
              </button>
            ) : (
              <Icon name="map-pin-area" size={15} weight="fill" style={{ color: "var(--accent-text)", flex: "none", marginRight: 1 }} />
            )}
            {projectsQ.data!.map((p) => {
              // Auf der Gesamtkarte ist KEIN Projekt aktiv — der Pin davor traegt den Zustand.
              const on = p.id === lastProjectId && !pathname.startsWith("/uebersicht");
              return (
                <button
                  key={p.id}
                  onClick={() => onPickProject(p.id)}
                  className="ring-focus"
                  aria-current={on ? "true" : undefined}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 9,
                    border: "1px solid",
                    borderColor: on ? "var(--accent-ring)" : "transparent",
                    background: on ? "var(--accent)" : "transparent",
                    color: on ? "#fff" : "var(--text-dim)",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    transition: "all .15s",
                  }}
                  onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = "var(--hover)"; vorgriffProjekt(p.id); }}
                  onFocus={() => vorgriffProjekt(p.id)}
                  onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = "transparent"; }}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {/* Kommandoleiste OHNE Ausloeser: kein sichtbares Element im Header mehr
            (die alte Pille blitzte beim Routenwechsel auf), nur die Tastenkuerzel
            F, "/" und ⌘K plus das Fenster selbst. Nicht auf der Karte (dort
            besitzt die Kartensuche dieselben Tasten) und nicht im Dashboard (dort
            haengt die Hero-Leiste) — zwei Instanzen oeffneten sonst zwei Fenster. */}
        {!pathname.startsWith("/project") && !pathname.startsWith("/karte") && <CommandBar variant="hidden" />}
        <IconBtn icon={theme === "dark" ? "sun" : "moon"} onClick={toggleTheme} title="Theme" />
        {/* Der Demo-Hinweis steht jetzt als klebender Chip unter der Kopfzeile
            (DemoChip im Geruest) — hier schob er auf dem Telefon die
            Bedienelemente rechts aus dem Bild. */}
        {auth ? (
          <AvatarMenu />
        ) : (
          <Btn icon="sign-in" onClick={() => navigate("/login")}>
            {t("common.login")}
          </Btn>
        )}
      </div>
      <style>{`@media(max-width:640px){ .topnav-label{ display:none; } } .uebersicht-mobil{ display:none; } @media(max-width:768px){ .psw-nav{ display:none !important; } .uebersicht-mobil{ display:inline-flex; } }`}</style>
    </header>
  );
}
